import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function encodeB64(raw: string): string {
  try {
    return btoa(unescape(encodeURIComponent(raw)));
  } catch {
    try {
      return btoa(raw);
    } catch {
      return '';
    }
  }
}

function decodeB64(b64: string): string {
  try {
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    try {
      return atob(b64);
    } catch {
      return '';
    }
  }
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const AppAccessLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const from = useMemo(() => {
    try {
      return (location.state as any)?.from || '/';
    } catch {
      return '/';
    }
  }, [location.state]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Login - Takagi App Download';
    try {
      const tokenEmail = sessionStorage.getItem('agencyEmail') || '';
      if (tokenEmail) {
        setEmail(tokenEmail);
        setEmailLocked(true);
      } else {
        const savedEmail = localStorage.getItem('accessEmail') || '';
        if (savedEmail) setEmail(savedEmail);
        setEmailLocked(false);
      }

      // Migrate legacy raw password -> base64 once
      const legacyPassword = localStorage.getItem('accessPassword') || '';
      const existingB64 = localStorage.getItem('accessPasswordB64') || '';
      if (!existingB64 && legacyPassword) {
        localStorage.setItem('accessPasswordB64', encodeB64(legacyPassword));
        localStorage.removeItem('accessPassword');
      }

      const savedPasswordB64 = localStorage.getItem('accessPasswordB64') || '';
      if (savedPasswordB64) {
        setPassword(decodeB64(savedPasswordB64));
      }
    } catch {
      // ignore
    }
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (changePasswordMode) {
      if (!newPassword || !confirmPassword) {
        setError('新しいパスワードを2回入力してください。');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('パスワードが一致しません。');
        return;
      }
      try {
        localStorage.setItem('accessPasswordB64', encodeB64(newPassword));
      } catch {
        // ignore
      }
      setPassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setChangePasswordMode(false);
      setError(null);
      return;
    }

    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください。');
      return;
    }

    if (!isValidEmail(email)) {
      setError('メールアドレスが無効です。');
      return;
    }

    try {
      localStorage.setItem('accessEmail', email);
      localStorage.setItem('accessPasswordB64', encodeB64(password));
      // cleanup legacy key if any
      localStorage.removeItem('accessPassword');
    } catch {
      // ignore
    }

    setError(null);
    navigate(from, { replace: true });
  };

  return (
    <div style={{
      fontFamily: 'Noto Sans JP, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      flexDirection: 'column',
      backgroundColor: '#fff', margin: 0, padding: '1rem',
      minHeight: '100vh'
    }}>
      <div><img src="/logo/logo.svg" alt="Takagi Logo" style={{ width: 180, marginBottom: '1.5rem' }} /></div>
      <div style={{ backgroundColor: '#f5f5f5', padding: '2rem', borderRadius: 8, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: 350, maxWidth: '90%', textAlign: 'center' }}>
        <h2 style={{ marginTop: 0, color: '#333', marginBottom: '1.5rem', fontWeight: 700 }}>ログイン</h2>
        <form onSubmit={onSubmit}>
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <label htmlFor="accessEmail" style={{ display: 'block', marginBottom: '.5rem', fontWeight: 500 }}>メールアドレス:</label>
            <input
              id="accessEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              readOnly={emailLocked}
              style={{ width: '100%', padding: '.8rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
            />
          </div>
          {!changePasswordMode && (
            <>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <label htmlFor="accessPassword" style={{ display: 'block', marginBottom: '.5rem', fontWeight: 500 }}>パスワード:</label>
                <input
                  id="accessPassword"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ width: '100%', padding: '.8rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setChangePasswordMode(true);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  style={{ background: 'transparent', border: 'none', color: '#0077C8', cursor: 'pointer', padding: 0 }}
                >
                  パスワードを変更
                </button>
              </div>
            </>
          )}

          {changePasswordMode && (
            <>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '.5rem', fontWeight: 500 }}>新しいパスワード:</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: '100%', padding: '.8rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '.5rem', fontWeight: 500 }}>新しいパスワード（確認）:</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ width: '100%', padding: '.8rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: '-0.25rem', marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setChangePasswordMode(false);
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  style={{ background: 'transparent', border: 'none', color: '#555', cursor: 'pointer', padding: 0 }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  style={{ background: 'transparent', border: 'none', color: '#0077C8', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                >
                  保存
                </button>
              </div>
            </>
          )}
          {error && (
            <div style={{ color: '#e74c3c', marginTop: '1rem', fontSize: '.9rem' }}>{error}</div>
          )}
          {!changePasswordMode && (
            <button
              type="submit"
              style={{ width: '100%', backgroundColor: '#00a0e9', color: '#fff', border: 'none', padding: '.8rem', borderRadius: 4, cursor: 'pointer', fontSize: '1rem', fontWeight: 500, marginTop: '1rem' }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0080c7')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#00a0e9')}
            >ログイン</button>
          )}
        </form>
      </div>
    </div>
  );
};

export default AppAccessLoginPage;
